import get from 'lodash/get';
import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import AsyncTableComponent from '../Table/AsyncTableContainer';

import CheckmarkOrXComponent from '../Table/CheckmarkOrXComponent';

import { formatDate, isAdmin, isPM, isSuperOrConcierge } from '../../utils';
import { getExportTitle } from '../ViewExport/utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../Table/constants';

import { DATE_RANGES_TYPES, TABLE_DATA_TYPES } from '../../staticData';
import { PinButton } from '../Button';
import StyledLink from '../StyledLink';
import ListActions from '../Table/ListActions';
import { limitFilter } from '../Table/filters';
import BuildingTableExport from './BuildingTableExport';
import {
  StyledBuildingNameContainer,
  StyledPinButton,
} from './styledComponents';

const BuildingTableComponent = (props) => {
  const { t } = useTransl();
  const theme = useTheme();
  const {
    items,
    getItems,
    listParams,
    listKey,
    openDoor,
    onDelete,
    pmcId,
    pmc,
    onExport,
    pinBuilding,
    unpinBuilding,
    getIsPinnedBuilding,
    match,
  } = props;

  const columns = [
    {
      Header: t('title.name'),
      id: 'name',
      accessor: ({ id, name }) => {
        const isPinnedBuilding = getIsPinnedBuilding(id);

        const onClick = () =>
          isPinnedBuilding ? unpinBuilding(id) : pinBuilding(id, name);

        return (
          <StyledBuildingNameContainer>
            <StyledLink to={`/building/${id}`}>{name}</StyledLink>
            {isPinnedBuilding ? (
              <PinButton onClick={onClick} />
            ) : (
              <StyledPinButton onClick={onClick} />
            )}
          </StyledBuildingNameContainer>
        );
      },
      headerClassName: 'sort',
      minWidth: 150,
    },
    {
      Header: t('title.address'),
      id: 'address_address1',
      accessor: 'address.address1',
      headerClassName: 'sort',
    },
    {
      Header: t('title.city'),
      id: 'address_city',
      accessor: 'address.city',
      headerClassName: 'sort',
    },
    {
      Header: t('title.state'),
      id: 'address_state',
      accessor: 'address.state',
      headerClassName: 'sort',
      width: 90,
    },
    {
      Header: t('title.units'),
      id: 'units_count',
      accessor: 'unitsCount',
      width: 70,
      headerClassName: 'sort',
    },
    {
      Header: t('title.pmc'),
      id: 'pmc',
      accessor: ({ id, parent }) =>
        isSuperOrConcierge({ buildingId: id }) ? (
          <span>{parent.name}</span>
        ) : (
          <StyledLink to={`/pmc/${parent.id}`}>{parent.name}</StyledLink>
        ),
      headerClassName: 'sort',
    },
    {
      Header: t('title.type'),
      accessor: 'type',
      id: 'type',
      width: 80,
      headerClassName: 'sort',
    },
    {
      Header: t('building.launchedCarson'),
      id: 'launch_date',
      accessor: ({ launchDate }) => formatDate(launchDate, true, 'MM/DD/YYYY'),
      width: 110,
      headerClassName: 'sort',
    },
    {
      Header: t('title.softwareOnly'),
      id: 'software_only',
      headerClassName: 'sort',
      width: 120,
      filter: false,
      accessor: ({ softwareOnly }) => (
        <CheckmarkOrXComponent value={softwareOnly} />
      ),
    },
    {
      Header: t('building.buildingIsFrozen'),
      id: 'frozen',
      headerClassName: 'admin-feature sort',
      filter: false,
      show: isAdmin(),
      width: 120,
      accessor: ({ frozen }) => <CheckmarkOrXComponent value={frozen} />,
    },
    {
      Header: t('title.actions'),
      id: 'actions',
      fixed: 'right',
      sortable: false,
      width: 120,
      accessor: (entry) => {
        const title = isPM(match?.params)
          ? t('doors.buzzOpenDoor')
          : t('title.actions');

        let actions = [];
        let openDoorActions = [];
        let adminActions = [];

        openDoorActions = get(entry, 'doors', []).map((door) => ({
          label: t('doors.openDoor', { doorName: door.name }),
          onClick: () => openDoor(door),
        }));
        if (isAdmin() && entry.id) {
          adminActions = [
            {
              label: t('actions.update'),
              url: `/building/${entry.id}/setting-v2`,
            },
            {
              label: t('actions.remove'),
              color: theme.palette.secondary.main,
              onClick: () => onDelete(entry, listParams),
            },
          ];
        }

        actions = actions.concat(openDoorActions, adminActions);

        return <ListActions actions={actions} title={title} isFullWidth />;
      },
    },
  ];

  const filters = [limitFilter(listParams)];

  const action = [
    {
      label: t('building.createBuilding'),
      url: '/building/create',
      show: isAdmin(),
    },
  ];

  const defaultParams = {
    ordering: 'name',
    pmc: pmcId,
  };
  const tableDataType = TABLE_DATA_TYPES.buildings;

  const title = t('title.buildings');
  const exportTitle = getExportTitle(title, pmc);
  const exportActions = (
    <BuildingTableExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );

  return (
    <AsyncTableComponent
      tableId={TABLE_ID.buildingsTable}
      title={title}
      items={items}
      columns={columns}
      defaultSort={TABLE_CONSTANTS.name}
      defaultSortDesc={false}
      manual
      filters={filters}
      onLoad={() => {}}
      getItems={getItems}
      listParams={listParams}
      defaultParams={defaultParams}
      tableDataType={tableDataType}
      listKey={listKey}
      action={<ListActions actions={action} />}
      dateRangeType={DATE_RANGES_TYPES.created_at}
      datePicker
      showFiltersAreApplied={false}
      exportActions={exportActions}
    />
  );
};

BuildingTableComponent.defaultProps = {
  pmcId: null,
  pmc: null,
};

BuildingTableComponent.propTypes = {
  onDelete: PropTypes.func.isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  items: PropTypes.instanceOf(Object).isRequired,
  pmcId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pmc: PropTypes.instanceOf(Object),
  onExport: PropTypes.func.isRequired,
  pinBuilding: PropTypes.func.isRequired,
  unpinBuilding: PropTypes.func.isRequired,
  getIsPinnedBuilding: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  openDoor: PropTypes.func.isRequired,
  listKey: PropTypes.string.isRequired,
  match: PropTypes.instanceOf(Object).isRequired,
};

export default BuildingTableComponent;
